/**
 * The one legend row every dock chart draws (spec §B2): 20 px, 11 px text,
 * swatch + name items on the left, an optional slot on the right (the chart's
 * title, an ⓘ for the "drag to select · click to walk" hint). It never wraps:
 * items keep their own width and the row clips at its end, because a legend
 * that wraps takes a row from a plot that only has ~200 px to begin with.
 *
 * Used by ClearanceChart, ClearanceSectionChart, StationVolumeChart and
 * StationSectionChart, which each had their own legend markup — and their own
 * swatch geometry, so "violation" was a 10×5 rect in one and a 12×12 square in
 * the other.
 */
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { DOCK_TEXT } from './dockStyles';

/**
 * How the mark looks in the chart, so the swatch looks the same:
 *  - `box` — a filled square (a bar, a category cell).
 *  - `barUp` / `barDown` — the upper / lower half of a box: a bar above or
 *    below the zero line (fill above, cut below).
 *  - `line` — a 2 px stroke; `dashed` — a dashed stroke (a design line).
 *  - `band` — a translucent wash (a tolerance band, a selection).
 *  - `tick` — a short 3 px bar under the baseline (the near-miss strip).
 */
export type DockSwatchShape = 'box' | 'barUp' | 'barDown' | 'line' | 'dashed' | 'band' | 'tick';

export interface DockLegendItem {
  key: string;
  label: ReactNode;
  /** Any CSS colour, including `var(--sv-…)`. */
  color?: string;
  shape?: DockSwatchShape;
  /** A custom swatch (a hatch, a ramp) — replaces `color` + `shape`. */
  swatch?: ReactNode;
  /** A muted qualifier after the label ("above design"). */
  detail?: ReactNode;
  /** The item's tooltip — the full sentence behind a short label. */
  title?: string;
  testId?: string;
}

/** The 12×12 swatch for a legend item; exported for a legend built by hand. */
export const DockSwatch = ({ color = 'currentColor', shape = 'box' }: { color?: string; shape?: DockSwatchShape }) => (
  <svg width={12} height={12} aria-hidden className='shrink-0'>
    {shape === 'box' && <rect x={1} y={1} width={10} height={10} rx={1.5} fill={color} />}
    {shape === 'barUp' && <rect x={1} y={1} width={10} height={5} fill={color} />}
    {shape === 'barDown' && <rect x={1} y={6} width={10} height={5} fill={color} />}
    {shape === 'line' && <line x1={0} y1={6} x2={12} y2={6} stroke={color} strokeWidth={2} />}
    {shape === 'dashed' && <line x1={0} y1={6} x2={12} y2={6} stroke={color} strokeWidth={1.5} strokeDasharray='4,2' />}
    {shape === 'band' && <rect x={0} y={2} width={12} height={8} fill={color} fillOpacity={0.22} />}
    {shape === 'tick' && <rect x={1} y={8} width={10} height={3} fill={color} />}
  </svg>
);

export interface DockChartLegendProps {
  items: DockLegendItem[];
  /** The right end — a chart title, an ⓘ hint, a metric toggle. */
  right?: ReactNode;
  /** Names the legend for assistive tech; the swatches are `aria-hidden`. */
  ariaLabel?: string;
  testId?: string;
  className?: string;
}

export const DockChartLegend = ({ items, right, ariaLabel, testId, className }: DockChartLegendProps) => (
  <div
    data-testid={testId}
    className={cn(
      // Phone-width container: a long right-hand note (the offset convention)
      // drops to its own line instead of squeezing the items out of sight.
      'flex h-5 shrink-0 items-center gap-3 overflow-hidden whitespace-nowrap text-slate-600 dark:text-stone-400 @max-[560px]:h-auto @max-[560px]:flex-wrap @max-[560px]:gap-y-1',
      DOCK_TEXT.meta,
      className,
    )}
  >
    <ul
      aria-label={ariaLabel}
      // Below a 560 px container (a phone) the items scroll sideways on one
      // line instead of the last ones being clipped ("Envelop…"); wrapping
      // would take the drawing's height.
      className='flex min-w-0 items-center gap-3 overflow-hidden @max-[560px]:min-w-[50%] @max-[560px]:grow @max-[560px]:basis-0 @max-[560px]:overflow-x-auto @max-[560px]:[scrollbar-width:none] @max-[560px]:[&::-webkit-scrollbar]:hidden'
    >
      {items.map((item) => (
        <li key={item.key} data-testid={item.testId} title={item.title} className='flex shrink-0 items-center gap-1'>
          {item.swatch ?? <DockSwatch color={item.color} shape={item.shape} />}
          <span>{item.label}</span>
          {item.detail != null && <span className='opacity-70'>{item.detail}</span>}
        </li>
      ))}
    </ul>
    {right != null && <div className='ml-auto flex shrink-0 items-center gap-2'>{right}</div>}
  </div>
);
